import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Camera, Video, Upload, Trash2, Pencil, Globe, Lock, Link2, Copy,
  CheckCircle2, Loader2, Play, ChevronLeft, ChevronRight, X, Code, Image as ImageIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "tous",    labelKey: "galerieTous",    color: "#64748B" },
  { key: "avant",   labelKey: "galerieAvant",   color: "#8B5CF6" },
  { key: "pendant", labelKey: "galeriePendant", color: "#3B82F6" },
  { key: "apres",   labelKey: "galerieApres",   color: "#10B981" },
];

const CAT_BADGE = {
  avant:   { bg: "bg-purple-50",   text: "text-purple-600",  border: "border-purple-200" },
  pendant: { bg: "bg-blue-50",     text: "text-blue-600",    border: "border-blue-200" },
  apres:   { bg: "bg-emerald-50",  text: "text-emerald-600", border: "border-emerald-200" },
};

function CategoriePill({ cat, t }) {
  const s = CAT_BADGE[cat] || CAT_BADGE.pendant;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      {t(`galerie${cat.charAt(0).toUpperCase() + cat.slice(1)}`)}
    </span>
  );
}

export default function GalerieTab({ chantier, onUpdate }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [catFilter, setCatFilter] = useState("tous");
  const [galeriePublique, setGaleriePublique] = useState(chantier.galerie_publique || false);
  const [galerieSlug, setGalerieSlug] = useState(chantier.galerie_slug || "");
  const [selectedForm, setSelectedForm] = useState({ categorie: "pendant", legende: "" });
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editLegende, setEditLegende] = useState("");
  const [showEmbed, setShowEmbed] = useState(false);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const { t } = useLanguage();
  const { isPatron } = useAuth();

  useEffect(() => { fetchMedia(); }, [chantier.id]);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${chantier.id}/galerie`);
      setMedia(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    if (isVideo && file.size > 50 * 1024 * 1024) {
      toast.error(t('galerieVideoTooLarge')); return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("legende", selectedForm.legende);
      formData.append("categorie", selectedForm.categorie);
      await axios.post(`${API}/chantiers/${chantier.id}/galerie`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded / e.total) * 100)),
      });
      toast.success(isVideo ? t('galerieVideoAdded') : t('galeriePhotoAdded'));
      setSelectedForm({ categorie: "pendant", legende: "" });
      fetchMedia();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errorGeneric'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteMedia = async (id) => {
    try {
      await axios.delete(`${API}/chantiers/${chantier.id}/galerie/${id}`);
      setMedia(prev => prev.filter(m => m.id !== id));
      toast.success(t('galerieMediaDeleted'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const saveLegende = async (id) => {
    try {
      await axios.put(`${API}/chantiers/${chantier.id}/galerie/${id}`, { legende: editLegende });
      setMedia(prev => prev.map(m => m.id === id ? { ...m, legende: editLegende } : m));
      setEditingId(null);
      toast.success(t('galerieLegendeSaved'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const updateCategorie = async (id, categorie) => {
    try {
      await axios.put(`${API}/chantiers/${chantier.id}/galerie/${id}`, { categorie });
      setMedia(prev => prev.map(m => m.id === id ? { ...m, categorie } : m));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const togglePublic = async () => {
    const newVal = !galeriePublique;
    try {
      const res = await axios.put(`${API}/chantiers/${chantier.id}/galerie/public`, { publique: newVal });
      setGaleriePublique(res.data.galerie_publique);
      setGalerieSlug(res.data.galerie_slug);
      toast.success(newVal ? t('galeriePublished') : t('galerieUnpublished'));
    } catch (e) { toast.error(t('errorGeneric')); }
  };

  const copyLink = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const filtered = catFilter === "tous" ? media : media.filter(m => m.categorie === catFilter);
  const publicUrl = galerieSlug ? `${window.location.origin}/galerie/${galerieSlug}` : "";
  const embedCode = `<iframe src="${publicUrl}" width="100%" height="600" frameborder="0" allowfullscreen style="border-radius:12px;"></iframe>`;

  // Lightbox navigation
  const goPrev = useCallback(() => setLightboxIdx(i => (i > 0 ? i - 1 : filtered.length - 1)), [filtered.length]);
  const goNext = useCallback(() => setLightboxIdx(i => (i < filtered.length - 1 ? i + 1 : 0)), [filtered.length]);

  if (loading) return (
    <div className="space-y-3">
      {[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="galerie-tab">

      {/* Upload Section */}
      {isPatron() && (
        <div className="bg-[#F8FAFC] border border-dashed border-[#CBD5E1] rounded-2xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#475569]">{t('galerieCategorie')}</label>
              <div className="flex gap-1 flex-wrap">
                {CATEGORIES.filter(c => c.key !== "tous").map(c => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setSelectedForm(f => ({ ...f, categorie: c.key }))}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all border ${selectedForm.categorie === c.key ? 'text-white border-transparent' : 'bg-white border-[#E2E8F0] text-[#64748B]'}`}
                    style={selectedForm.categorie === c.key ? { background: c.color, borderColor: c.color } : {}}
                  >
                    {t(c.labelKey)}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#475569]">{t('galerieLegende')}</label>
              <Input
                value={selectedForm.legende}
                onChange={e => setSelectedForm(f => ({ ...f, legende: e.target.value }))}
                placeholder={t('galerieLegendePlaceholder')}
                className="h-8 text-xs bg-white border-[#E2E8F0]"
              />
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleFileSelect}
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-[#3B82F6] hover:bg-blue-600 text-white rounded-xl h-12 font-medium"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {uploadProgress}%
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {t('galerieUploadBtn')}
              </span>
            )}
          </Button>
          {uploading && (
            <div className="w-full bg-[#E2E8F0] rounded-full h-1.5">
              <div className="bg-[#3B82F6] h-1.5 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
        </div>
      )}

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATEGORIES.map(c => {
          const count = c.key === "tous" ? media.length : media.filter(m => m.categorie === c.key).length;
          return (
            <button
              key={c.key}
              onClick={() => setCatFilter(c.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${catFilter === c.key ? 'text-white shadow-sm' : 'bg-white border border-[#E2E8F0] text-[#64748B]'}`}
              style={catFilter === c.key ? { background: c.color } : {}}
            >
              {t(c.labelKey)}
              {count > 0 && <span className={`text-[10px] font-bold px-1 rounded-full ${catFilter === c.key ? 'bg-white/25' : 'bg-[#F1F5F9]'}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Media Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-[#94A3B8]">
          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="font-medium text-sm">{t('galerieEmpty')}</p>
          {isPatron() && <p className="text-xs mt-1">{t('galerieEmptyHint')}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              data-testid={`media-${item.id}`}
              className="relative group rounded-xl overflow-hidden aspect-square bg-[#F1F5F9] cursor-pointer"
              onClick={() => setLightboxIdx(idx)}
            >
              {item.type === "video" ? (
                <div className="w-full h-full bg-[#1E293B] flex items-center justify-center">
                  <video src={item.url} className="w-full h-full object-cover opacity-80" muted preload="metadata" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center shadow-lg">
                      <Play className="w-5 h-5 text-[#1E293B] ml-0.5" />
                    </div>
                  </div>
                </div>
              ) : (
                <img src={item.url} alt={item.legende || item.filename} className="w-full h-full object-cover" />
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all" />

              {/* Category badge */}
              <div className="absolute top-1.5 left-1.5">
                <CategoriePill cat={item.categorie} t={t} />
              </div>

              {/* Delete button */}
              {isPatron() && (
                <button
                  onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                  className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}

              {/* Legende */}
              {item.legende && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs truncate">{item.legende}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Public Gallery Section — Patron only */}
      {isPatron() && (
        <div className="border border-[#E2E8F0] rounded-2xl p-4 space-y-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {galeriePublique
                ? <Globe className="w-4 h-4 text-emerald-500" />
                : <Lock className="w-4 h-4 text-[#94A3B8]" />
              }
              <span className="font-semibold text-sm text-[#1E293B]">
                {galeriePublique ? t('galeriePubliqueActive') : t('galeriePubliqueOff')}
              </span>
            </div>
            <button
              data-testid="toggle-public-btn"
              onClick={togglePublic}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${galeriePublique ? 'bg-emerald-500' : 'bg-[#CBD5E1]'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${galeriePublique ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {galeriePublique && publicUrl && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-3 py-2">
                <Link2 className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
                <span className="text-xs text-[#475569] truncate flex-1">{publicUrl}</span>
                <button
                  onClick={() => copyLink(publicUrl)}
                  className="flex items-center gap-1 text-xs font-medium text-[#3B82F6] hover:text-blue-700 flex-shrink-0"
                >
                  {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? t('copied') : t('copy')}
                </button>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowEmbed(true)}
                className="w-full border-[#E2E8F0] text-[#475569] rounded-xl text-xs h-8"
              >
                <Code className="w-3.5 h-3.5 mr-1.5" />
                {t('galerieEmbedCode')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && filtered[lightboxIdx] && (
        <div
          className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
        >
          <button className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10" onClick={() => setLightboxIdx(null)}>
            <X className="w-6 h-6" />
          </button>
          {filtered.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10" onClick={(e) => { e.stopPropagation(); goPrev(); }}>
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white z-10" onClick={(e) => { e.stopPropagation(); goNext(); }}>
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="max-w-3xl w-full px-16" onClick={e => e.stopPropagation()}>
            {filtered[lightboxIdx].type === "video" ? (
              <video
                src={filtered[lightboxIdx].url}
                controls
                autoPlay
                className="w-full max-h-[75vh] rounded-xl shadow-2xl"
              />
            ) : (
              <img
                src={filtered[lightboxIdx].url}
                alt={filtered[lightboxIdx].legende}
                className="w-full max-h-[75vh] object-contain rounded-xl shadow-2xl"
              />
            )}
            <div className="mt-3 flex items-center gap-3">
              <CategoriePill cat={filtered[lightboxIdx].categorie} t={t} />
              {isPatron() && editingId === filtered[lightboxIdx].id ? (
                <div className="flex-1 flex gap-2">
                  <Input
                    value={editLegende}
                    onChange={e => setEditLegende(e.target.value)}
                    className="flex-1 h-8 text-sm bg-white/10 border-white/20 text-white placeholder-white/50"
                    placeholder={t('galerieLegende')}
                    autoFocus
                  />
                  <Button size="sm" onClick={() => saveLegende(filtered[lightboxIdx].id)} className="bg-emerald-500 text-white h-8 px-3 rounded-lg">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-white/20 text-white h-8 px-3 rounded-lg">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-white/80 text-sm flex-1">{filtered[lightboxIdx].legende || t('galerieNoLegende')}</span>
                  {isPatron() && (
                    <button
                      onClick={() => { setEditingId(filtered[lightboxIdx].id); setEditLegende(filtered[lightboxIdx].legende || ""); }}
                      className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Category changer (patron) */}
            {isPatron() && (
              <div className="mt-2 flex gap-1">
                {CATEGORIES.filter(c => c.key !== "tous").map(c => (
                  <button
                    key={c.key}
                    onClick={() => updateCategorie(filtered[lightboxIdx].id, c.key)}
                    className={`px-2 py-0.5 rounded-lg text-[10px] font-medium transition-all border ${filtered[lightboxIdx].categorie === c.key ? 'text-white border-transparent' : 'bg-white/10 border-white/20 text-white/70'}`}
                    style={filtered[lightboxIdx].categorie === c.key ? { background: c.color } : {}}
                  >
                    {t(c.labelKey)}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 text-center text-white/40 text-xs">
              {lightboxIdx + 1} / {filtered.length}
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Modal */}
      {showEmbed && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowEmbed(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-5 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#1E293B] flex items-center gap-2"><Code className="w-4 h-4 text-[#3B82F6]" />{t('galerieEmbedCode')}</h3>
              <button onClick={() => setShowEmbed(false)} className="p-1 hover:bg-[#F1F5F9] rounded-lg"><X className="w-4 h-4 text-[#94A3B8]" /></button>
            </div>
            <p className="text-sm text-[#64748B] mb-3">{t('galerieEmbedHint')}</p>
            <div className="bg-[#0F2744] rounded-xl p-3 mb-3 overflow-x-auto">
              <code className="text-xs text-emerald-400 whitespace-pre-wrap break-all">{embedCode}</code>
            </div>
            <Button
              onClick={() => { copyLink(embedCode); setShowEmbed(false); }}
              className="w-full bg-[#3B82F6] text-white rounded-xl"
            >
              <Copy className="w-4 h-4 mr-2" />{t('copyEmbedCode')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
